import type { UserProfileResponse } from '@models';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { logger } from '@utils/logger';

/**
 * User interface representing a user in the system
 * Updated to match both Logto user claims and backend User model
 */

/**
 * Authentication state interface
 */
export interface CachedUserInfo {
  id: string | null;
  slug: string | null;
  full_name: string | null;
  profile_picture: string | null;
}

interface AuthState {
  isAuthenticated: boolean;
  user: UserProfileResponse | null;
  cachedUserInfo: CachedUserInfo | null;
  wasAuthenticated: boolean;
  loading: boolean;
  error: string | null;
  /**
   * True from the first statement of `executeSignOut`. A new sign-in does NOT
   * simply release it: `signIn` clears it only when Logto is signed out too,
   * because a web sign-out that failed leaves the adapter still authenticated
   * on the old bearer, and releasing there would re-enable the current-user
   * query against the leaving session. Otherwise it is released by the document
   * being replaced, which every sign-in route out of that document ends in, and
   * the next document starts false because this field is not persisted.
   *
   * The one thing in this store that says "this document is leaving",
   * which `clearAuthState` cannot say: it flips `isAuthenticated` here, but the
   * app's own notion of being signed in is derived from the current-user QUERY
   * (`AuthProvider`'s `isFullyAuthenticated`), and that query refetches
   * straight back into a session that is being torn down.
   *
   * AIDEV-NOTE: deliberately absent from `partialize` below, so it never
   * reaches `auth-storage` and cannot survive into the next document.
   */
  isSigningOut: boolean;
}

/**
 * Authentication actions interface
 */
interface AuthActions {
  // Logto integration
  setAuthenticated: (authenticated: boolean) => void;
  setSigningOut: (signingOut: boolean) => void;
  setCurrentUser: (backendUser: UserProfileResponse | null) => void;
  clearCachedAuth: () => void;

  // Profile management
  updateProfile: (updates: Partial<UserProfileResponse>) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;

  // Legacy methods (deprecated but kept for compatibility)
  logout: () => void;
  login: (email: string, password: string) => Promise<void>;
  loginWithLinkedIn: () => Promise<void>;
  initializeMockUser: () => void;
}

/**
 * Authentication store using Zustand with persistence
 */
export const useAuthStore = create<AuthState & AuthActions>()(
  persist(
    (set, get) => ({
      // Initial state - no longer hardcoded
      isAuthenticated: false,
      user: null,
      cachedUserInfo: null,
      wasAuthenticated: false,
      loading: false,
      error: null,
      isSigningOut: false,

      setAuthenticated: (authenticated: boolean) => {
        set({ isAuthenticated: authenticated });
      },

      setSigningOut: (signingOut: boolean) => {
        set({ isSigningOut: signingOut });
      },

      setCurrentUser: (backendUser: UserProfileResponse | null) => {
        if (!backendUser) {
          set({ user: null, isAuthenticated: false });
          return;
        }

        set({
          user: backendUser,
          isAuthenticated: true,
          cachedUserInfo: {
            id: backendUser.id,
            slug: backendUser.slug,
            full_name: backendUser.full_name,
            profile_picture: backendUser.profile_picture ?? null,
          },
          wasAuthenticated: true,
          loading: false,
          error: null,
        });
      },

      clearCachedAuth: () => {
        set({ cachedUserInfo: null, wasAuthenticated: false });
      },

      updateProfile: (updates: Partial<UserProfileResponse>) => {
        const { user } = get();
        if (user) {
          set({
            user: { ...user, ...updates },
          });
        }
      },

      setLoading: (loading: boolean) => {
        set({ loading });
      },

      setError: (error: string | null) => {
        set({ error });
      },

      // Legacy methods - kept for compatibility but now deprecated
      logout: () => {
        set({
          isAuthenticated: false,
          user: null,
          cachedUserInfo: null,
          wasAuthenticated: false,
          error: null,
        });
      },

      login: () => {
        // Deprecated - now handled by Logto
        logger.warn('useAuthStore.login() is deprecated. Use Logto signIn() instead.');
        return Promise.resolve();
      },

      loginWithLinkedIn: () => {
        // Deprecated - now handled by Logto with LinkedIn connector
        logger.warn(
          'useAuthStore.loginWithLinkedIn() is deprecated. Use Logto with LinkedIn connector instead.',
        );
        return Promise.resolve();
      },

      initializeMockUser: () => {
        // Deprecated - now using real Logto users
        logger.warn(
          'useAuthStore.initializeMockUser() is deprecated. Use setCurrentUser() instead.',
        );
      },
    }),
    {
      name: 'auth-storage',
      partialize: state => ({
        user: state.user,
        cachedUserInfo: state.cachedUserInfo,
        wasAuthenticated: state.wasAuthenticated,
      }),
    },
  ),
);
